import { FC, useEffect, useRef, InputHTMLAttributes } from 'react';
import { _js } from '@ifixit/localize';
import { InstantSearch as InstantSearchComponent } from '@ifixit/ui/components/instant-search';
import { cn } from '@ifixit/ui/lib/utils';
import { KeyboardNavigation } from 'Shared/keyboard_navigation';
import { getIndexes, getSearchClient, PUBLIC_KEY, isProUser } from 'Guide/searchClient';

interface InstantSearchBarProps {
   isOnCart?: boolean;
   isMobile?: boolean;
   /** Additional class name for the input element (merged via cn) */
   inputClassName?: string;
   /** Additional input props to pass through */
   inputProps?: InputHTMLAttributes<HTMLInputElement>;
}

/**
 * InstantSearchBar component that provides search functionality in the header
 * with keyboard shortcut support
 */
const InstantSearchBar: FC<InstantSearchBarProps> = ({
   isOnCart = false,
   inputClassName,
   inputProps: externalInputProps,
}) => {
   // Determine the appropriate placeholder text based on context
   const placeholder = isOnCart ? _js('Search by device or model #') : _js('Search');

   const searchClient = getSearchClient();
   const indices = getIndexes();
   const keyboardNavigationRef = useRef<typeof KeyboardNavigation | null>(null);

   useEffect(() => {
      // Set up keyboard shortcut to focus the search input
      const focusSearchInput = () => {
         const searchInput = document.querySelector('.instant-search-input') as HTMLInputElement;
         if (searchInput) searchInput.focus();
      };

      if (!keyboardNavigationRef.current) {
         // Initialize KeyboardNavigation only once
         keyboardNavigationRef.current = new KeyboardNavigation({
            keys: { '/': focusSearchInput },
         });
      }
   }, []);

   // Merge the default className with any external inputClassName and inputProps
   const mergedInputProps = {
      ...externalInputProps,
      className: cn('rounded-full', inputClassName, externalInputProps?.className),
   };

   return (
      <InstantSearchComponent
         searchClient={searchClient}
         apiKey={PUBLIC_KEY}
         indices={indices}
         isProUser={isProUser()}
         placeholder={placeholder}
         currency={App.userPreferredStoreCurrency}
         localeCode={App.userPreferredStoreLocale}
         storeCode={App.userPreferredStoreCode}
         shortcutKey="/"
         inputProps={mergedInputProps}
         popoverProps={{
            sideOffset: 20,
         }}
      />
   );
};

export default InstantSearchBar;
